import { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import CanonicalTag from '../components/CanonicalTag';
import techLabImage from '../assets/tech-lab-adu.jpg';

const HowToUseADUTechLab = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);
  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-backyard-tech-lab-or-maker-space" />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-6">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
                How to Use Your ADU as a Tech Lab or Maker Garage
              </h1>
              <div className="flex justify-center mb-8">
                <button 
                  onClick={openCalendlyModal}
                  className="bg-brand-orange text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                >
                  📅 Book & Schedule
                </button>
              </div>
            </div>

            {/* Main Image */}
            <div className="mb-16">
              <img 
                src={techLabImage} 
                alt="Tech Lab ADU" 
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Content Sections */}
            <div className="space-y-16">
              {/* Section 1 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Build, Tinker, Innovate—In a Dedicated Creative Lab
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  This ADU layout supports inventors, engineers, or STEM-based creators needing workspace for tech projects, robotics, or 3D printing.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>High-Tech Setup</h3>
                    <p className="text-muted-foreground">Workbenches with electrical access, 3D printers, and dual monitor setups</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Safety Features</h3>
                    <p className="text-muted-foreground">Safe tool walls, ventilation systems, and sound insulation for noisy builds</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Organization</h3>
                    <p className="text-muted-foreground">LED lighting, storage drawers, and pegboard systems for parts and tools</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => window.location.href = '/landing'}
                    className="bg-white text-brand-orange border-2 border-brand-orange px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange hover:text-white transition-colors"
                  >
                    📐 What Fits My Lot?
                  </button>
                  <button 
                    onClick={() => window.location.href = '/adu-calculator'}
                    className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                  >
                    💰 Get Your Price & ROI Report
                  </button>
                </div>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Space for Innovation
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#E4683B' }}>Essential Features</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Workbenches with electrical access</li>
                      <li>• Safe tool walls + ventilation</li>
                      <li>• LED lighting + storage drawers</li>
                      <li>• Sound insulation for noisy builds</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#E4683B' }}>Best For:</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Teens in STEM competitions</li>
                      <li>• Adult engineers & hobbyists</li>
                      <li>• Side hustle inventors</li>
                      <li>• Electronics and robotics enthusiasts</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Value Boost
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  A dedicated tech lab provides unique functionality that appeals to modern homebuyers and tech professionals.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Unique Value</h3>
                    <p className="text-muted-foreground">Functional space creates unique resale value in tech-forward markets</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Zoning Benefits</h3>
                    <p className="text-muted-foreground">Eligible for live/work zoning in many areas, increasing property flexibility</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Innovation Hub</h3>
                    <p className="text-muted-foreground">Perfect for developing prototypes, startups, or educational projects</p>
                  </div>
                </div>
              </section>
            </div>

            {/* Bottom CTAs */}
            <div className="text-center mt-16">
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.open('/our-work', '_blank')}
                  className="bg-white text-brand-orange border-2 border-brand-orange px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange hover:text-white transition-colors"
                >
                  🏡 Our Portfolio
                </button>
                <button 
                  onClick={() => window.open('https://uda-adu.com/faq', '_blank')}
                  className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                >
                  ❓ Our FAQ
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[90vh] m-4">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">Schedule a Consultation</h3>
                <button 
                  onClick={closeCalendlyModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <iframe 
                src="https://calendly.com/udaadu"
                width="100%" 
                height="100%" 
                frameBorder="0"
                title="Schedule a meeting"
                className="rounded-b-lg"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HowToUseADUTechLab;